import { useEffect, useMemo } from 'react';
import useAxios from 'axios-hooks';
import { IPlainObject, ScheduleDefaultModel } from '../../../models';
import { AxiosPromise, AxiosRequestConfig } from 'axios';
import {
  allDayOptions,
  AxiosMethod,
  dayOptions,
  defaultAxiosOptions,
} from '../../../constants';
import { OfferScheduleList } from '../ScheduleForm.types';
import { ResponseValuesOverride } from '../../../models/response-values-override';

export function useGetOfferGeoSchedules(
  endpoint: string,
): [
  ResponseValuesOverride<OfferScheduleList[]>,
  () => AxiosPromise<ScheduleDefaultModel[]>,
] {
  const config: AxiosRequestConfig = {
    method: AxiosMethod.Get,
    url: endpoint,
  };

  const [resp, execute] = useAxios<ScheduleDefaultModel[]>(config, {
    ...defaultAxiosOptions,
  });

  const list = useMemo((): OfferScheduleList[] => {
    if (!resp.data) {
      return [];
    }

    const data = resp.data.map((item) => ({
      ...item,
      timeFrom: item.timeFrom
        ? item.timeFrom.replace(/:[0-9]{2}$/, '')
        : item.timeFrom,
      timeTo: item.timeTo ? item.timeTo.replace(/:[0-9]{2}$/, '') : item.timeTo,
    }));

    let everyDaySchedule = [];

    const byTimes = data.reduce((acum, item) => {
      const { timeFrom, timeTo, day } = item;

      const value: IPlainObject<ScheduleDefaultModel> = {
        ...(acum[`${timeFrom}-${timeTo}`] || {}),
        [day]: item,
      };

      if (Object.keys(value).length === dayOptions.length) {
        everyDaySchedule = [
          ...everyDaySchedule,
          ...Object.values(value).map(({ id }) => id),
        ];
      }

      return {
        ...acum,
        [`${timeFrom}-${timeTo}`]: value,
      };
    }, {});

    const uniqData = data.filter(({ id }) => !everyDaySchedule.includes(id));

    const everyDays = Object.keys(byTimes)
      .map((key): OfferScheduleList => {
        if (Object.keys(byTimes[key]).length < dayOptions.length) {
          return null;
        }

        return {
          day: allDayOptions[dayOptions.length],
          isEveryDay: true,
          schedule: Object.values(byTimes[key]),
        };
      })
      .filter((v) => v);

    const scheduleByDays = dayOptions
      .map(
        (day, index): OfferScheduleList => ({
          day,
          schedule: uniqData.filter((item) => item.day === index),
        }),
      )
      .filter((item) => !!item.schedule.length);

    return [...everyDays, ...scheduleByDays];
  }, [resp.data]);

  useEffect(() => {
    execute();
  }, []);

  const getSchedule = () => execute();

  return [
    { ...resp, data: list } as unknown as ResponseValuesOverride<
      OfferScheduleList[]
    >,
    getSchedule,
  ];
}
